import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import showNotification from '../component/Notification';
import baseURL from '../component/baseURL';
import stringsoflanguages from '../component/stringsoflanguages';


export default function IncomingMemberFeed({ hideMobileHeader = false }) {
    const location = useLocation();
    const navigate = useNavigate();
    const [userID, setUserID] = useState(null);
    const [loading, setLoading] = useState(true);
    const [memberDetails, setMemberDetails] = useState(null);
    const [listings, setListings] = useState([]);
    const [acceptedOverrideByListing, setAcceptedOverrideByListing] = useState({});
    const [publishedOverrideByListing, setPublishedOverrideByListing] = useState({});
    const senderId = location.state?.sender_id ?? '';
    const requestId = location.state?.request_id ?? '';

    const [isMobile, setIsMobile] = useState(false);

    const headerTitle = useMemo(() => stringsoflanguages.incomingMemberFeed, []);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 767);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        const userid = localStorage.getItem('user_id');
        setUserID(userid);

        fetchIncomingMemberFeed(userid, senderId, requestId);
    }, [senderId, requestId]);



    function fetchIncomingMemberFeed(userid, senderId, requestId) {
        if (!userid) return;
        setLoading(true);
        let formdata = new FormData();
        formdata.append("user_id", userid)
        formdata.append("sender_id", senderId)
        formdata.append("request_id", requestId)
        formdata.append("key", "81dc9bdb52d04dc20036dbd8313ed055")
        fetch(baseURL.URL + '/portfolio_feed/view_feed', {
            method: 'POST',
            body: formdata
        }).then((response) => response.json())
            .then((responseJson) => {
                if (responseJson.server?.code === 200) {
                    const result = responseJson.response?.result ?? {};
                    const md = result.member_details ?? null;
                    const available = Array.isArray(result.available_listings) ? result.available_listings : [];
                    setMemberDetails(md);
                    setListings(available);
                    setAcceptedOverrideByListing({});
                    setPublishedOverrideByListing({});
                } else {
                    setMemberDetails(null);
                    setListings([]);
                    setAcceptedOverrideByListing({});
                    setPublishedOverrideByListing({});
                    showNotification('Error', responseJson.server?.message || 'Failed to load feed', 'danger');
                }
                setLoading(false);
            })
            .catch((error) => {
                console.error(error);
                setMemberDetails(null);
                setListings([]);
                setAcceptedOverrideByListing({});
                setPublishedOverrideByListing({});
                setLoading(false);
            });
    }


    function acceptDeclineFeed(nextValue, listingId) {
        if (!userID || !requestId || !listingId) return Promise.reject(new Error('Missing required data'));
        let formdata = new FormData();
        formdata.append("user_id", userID)
        formdata.append("response_type", String(nextValue))
        formdata.append("request_id", requestId)
        formdata.append("listing_id", listingId)
        formdata.append("on_off_toggle", String(nextValue))
        formdata.append("key", "81dc9bdb52d04dc20036dbd8313ed055")
        return fetch(baseURL.URL + '/portfolio_feed/accept_decline_feed_shared_single', {
            method: 'POST',
            body: formdata
        }).then((response) => response.json())
            .then((responseJson) => {
                if (responseJson.server?.code === 200) {
                    showNotification('Success', responseJson.server?.message || 'Updated', 'success');
                    fetchIncomingMemberFeed(userID, senderId, requestId);
                    return true;
                }
                const msg = responseJson.server?.message || 'Failed to update';
                showNotification('Error', msg, 'danger');
                throw new Error(msg);
            })
            .catch((error) => {
                console.error(error);
                showNotification('Error', error.message, 'danger');
                throw error;
            });
    }


    function publishUnpublishFeed(nextValue, listingId) {
        if (!userID || !requestId || !listingId) return Promise.reject(new Error('Missing required data'));
        let formdata = new FormData();
        formdata.append("user_id", userID)
        formdata.append("is_published", String(nextValue))
        formdata.append("request_id", requestId)
        formdata.append("listing_id", listingId)
        formdata.append("key", "81dc9bdb52d04dc20036dbd8313ed055")

        return fetch(baseURL.URL + '/portfolio_feed/publish_unpublish_feed_shared', {
            method: 'POST',
            body: formdata
        }).then((response) => response.json())
            .then((responseJson) => {
                if (responseJson.server?.code === 200) {
                    showNotification('Success', responseJson.server?.message || 'Updated', 'success');
                    fetchIncomingMemberFeed(userID, senderId, requestId);
                    return true;
                }
                const msg = responseJson.server?.message || 'Failed to update';
                showNotification('Error', msg, 'danger');
                throw new Error(msg);
            })
            .catch((error) => {
                console.error(error);
                showNotification('Error', error.message, 'danger');
                throw error;
            });
    }

    const isToggleOn = (v) => v === 1 || v === true || String(v) === '1';

    function listingToggleKey(listing, index) {
        return listing?.listing_id ?? listing?.feed_id ?? listing?.request_id ?? `idx-${index}`;
    }

    function handleAcceptedToggle(listing, index, nextChecked) {
        const listingId = listing?.listing_id;
        const key = listingToggleKey(listing, index);
        const nextValue = nextChecked ? '1' : '0';
        const prevChecked = acceptedOverrideByListing?.[key] ?? isToggleOn(listing?.status);

        if (!listingId) return;

        setAcceptedOverrideByListing((prev) => ({ ...prev, [key]: nextChecked }));
        acceptDeclineFeed(nextValue, listingId).catch(() => {
            setAcceptedOverrideByListing((prev) => ({ ...prev, [key]: prevChecked }));
        });
    }

    function handlePublishedToggle(listing, index, nextChecked) {
        const listingId = listing?.listing_id;
        const key = listingToggleKey(listing, index);
        const nextValue = nextChecked ? '1' : '0';
        const prevChecked = publishedOverrideByListing?.[key] ?? isToggleOn(listing?.is_published);

        if (!listingId) return;

        setPublishedOverrideByListing((prev) => ({ ...prev, [key]: nextChecked }));
        publishUnpublishFeed(nextValue, listingId).catch(() => {
            setPublishedOverrideByListing((prev) => ({ ...prev, [key]: prevChecked }));
        });
    }

    return (
        <div className="content-right incoming-member-feed-page">
            {!hideMobileHeader && isMobile && (
                <div className="mobile-page-header incoming-member-feed-header flex align-items-center">
                    <button
                        type="button"
                        className="mobile-back-btn"
                        onClick={() => navigate(-1)}
                        aria-label="Back"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M15 18L9 12L15 6" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                    <h2 className="mobile-page-title incoming-member-feed-title">{headerTitle}</h2>
                </div>
            )}

            <div className="incoming-member-feed-body">
                {loading ? (
                    <div className="page-loader-wrapper" style={{ padding: 16 }}>
                        <div className="react-js-loader">
                            <div className="react-js-loader-spinner"></div>
                            <p className="react-js-loader-text">{stringsoflanguages.loading}</p>
                        </div>
                    </div>
                ) : (
                    <div className="incoming-member-feed-content flex gap-4 w-full mb-4">
                        <div className="incoming-member-profile-card w-full cursor-pointer" onClick={() => {
                            navigate('/member-profile', {
                                state: {
                                    memberID: memberDetails?.user_id,
                                    member: memberDetails,
                                    from: '/feed/incoming-member-feed'
                                }
                            });
                        }}>
                            <div className="incoming-member-profile-left">
                                <div className="incoming-member-avatar">
                                    <img
                                        src={memberDetails?.user_image_thumb ?? memberDetails?.user_image ?? 'https://placehold.co/96x96'}
                                        alt={memberDetails?.user_name ?? 'Member'}
                                    />
                                </div>
                                <div className="incoming-member-meta">
                                    <div className="incoming-member-name">{memberDetails?.user_name ?? '—'}</div>
                                    <div className="incoming-member-location">
                                        <i className="fa-solid fa-location-dot" aria-hidden="true" />
                                        <span>{[memberDetails?.city_name, memberDetails?.country_name].filter(Boolean).join(' - ') || '—'}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="incoming-member-profile-right">
                                <img
                                    src={memberDetails?.company_image_thumb ?? memberDetails?.company_image ?? ''}
                                    alt="Company"
                                    style={{ width: 54, height: 30, objectFit: 'contain' }}
                                />
                            </div>
                        </div>


                        <div className="incoming-member-stats w-full">
                            <div className="incoming-member-stat">
                                <div className="incoming-member-stat-number">{memberDetails?.total_published ?? 0}</div>
                                <div className="incoming-member-stat-label">{stringsoflanguages.totalPublished}</div>
                            </div>
                            <div className="incoming-member-stat-divider" aria-hidden="true" />
                            <div className="incoming-member-stat">
                                <div className="incoming-member-stat-number">{memberDetails?.total_accepted ?? 0}</div>
                                <div className="incoming-member-stat-label">{stringsoflanguages.totalAccepted}</div>
                            </div>
                            <div className="incoming-member-stat-divider" aria-hidden="true" />
                            <div className="incoming-member-stat">
                                <div className="incoming-member-stat-number">{memberDetails?.total_shared ?? 0}</div>
                                <div className="incoming-member-stat-label">{stringsoflanguages.totalShared}</div>
                            </div>
                        </div>

                    </div>
                )}







                <div className="my-listings-cards-grid w-full">
                    <div className="property-grid">
                        {listings.length === 0 ? (
                            <p className="no-data-message">{stringsoflanguages.noListingsFound}</p>
                        ) : (
                            listings.map((listing, index) => {
                                const id = listing.listing_id ?? listing.feed_id ?? listing.request_id ?? index;
                                const toggleKey = listingToggleKey(listing, index);
                                const image = listing.image ?? 'https://placehold.co/600x400';
                                const title = listing.ListingId ?? listing.listing_id ?? '—';
                                const propertyType = listing.PropertyType ?? 'Property Type';
                                const streetPart = [listing.StreetNumber, listing.StreetName].filter(Boolean).join(' ').trim();
                                const cityParts = [listing.city_name, listing.state_name, listing.PostalCode, listing.country_name].filter(Boolean);
                                const cityPart = cityParts.join(', ').trim();
                                const address = streetPart ? (cityPart ? `${streetPart}, ${cityPart}` : streetPart) : (cityPart || '—');
                                const areaValue = listing.LivingArea ?? listing.LotSizeArea ?? null;
                                const rawUnit = listing.LivingAreaUnits ?? listing.LotSizeUnits ?? 'Sq Feet';
                                const areaUnitDisplay = /meter|metre/i.test(String(rawUnit)) ? 'm²' : 'ft²';
                                const area = areaValue != null && areaValue !== '' ? `${areaValue} ${areaUnitDisplay}` : '—';
                                const bedrooms = listing.BedroomsTotal ?? '—';
                                const bathrooms = listing.BathroomsTotalInteger ?? '—';
                                const parking = listing.ParkingTotal ?? listing.GarageSpaces ?? '—';
                                const price = listing.ListPrice ?? '—';
                                const status = (listing.StandardStatus ?? listing.StandardStatusName) || '—';

                                const agent = {
                                    avatar: memberDetails?.user_image_thumb ?? memberDetails?.user_image ?? '',
                                    name: memberDetails?.user_name ?? ([memberDetails?.fname, memberDetails?.lname].filter(Boolean).join(' ').trim() || '—')
                                };

                                return (
                                    <>



                                        <div
                                            key={id}
                                            className="property-card cursor-pointer"
                                            onClick={() => navigate('/portfolio-details', {
                                                state: {
                                                    listingID: listing.listing_id ?? listing.ListingId ?? id,
                                                    from: 'incoming-member-feed'
                                                }
                                            })}
                                        >

                                            <div className="property-image">
                                                <img src={image} alt="property" />
                                                <div className="agent-info">
                                                    <div className="agent-avatar">
                                                        <img src={agent.avatar || 'https://placehold.co/40x40'} alt="agent" />
                                                    </div>
                                                    <div className="agent-name">
                                                        <span>{agent.name}</span>
                                                    </div>
                                                </div>
                                                <div className="status">
                                                    {status === 'Active' && (
                                                        <>
                                                            <span className="status-dot active"></span>
                                                            <span>{status}</span>
                                                        </>
                                                    )}
                                                    {status === 'Coming Soon' && (
                                                        <>
                                                            <span className="status-dot coming-soon"></span>
                                                            <span>{status}</span>
                                                        </>
                                                    )}
                                                    {status === 'Sold' && (
                                                        <>
                                                            <span className="status-dot sold"></span>
                                                            <span>{status}</span>
                                                        </>
                                                    )}
                                                    {status === 'Cancelled' && (
                                                        <>
                                                            <span className="status-dot cancelled"></span>
                                                            <span>{status}</span>
                                                        </>
                                                    )}
                                                    {status === 'Under Offer' && (
                                                        <>
                                                            <span className="status-dot under-offer"></span>
                                                            <span>{status}</span>
                                                        </>
                                                    )}
                                                    {(!status || !['Active', 'Coming Soon', 'Sold', 'Cancelled', 'Under Offer'].includes(status)) && (
                                                        <>
                                                            <span className="status-dot"></span>
                                                            <span>{status || 'N/A'}</span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="property-content">
                                                <h3 className="property-title">{title} <span className="type ml-4">{propertyType}</span></h3>
                                                <div className="location">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                        <path d="M10.0001 1.81818C6.49118 1.81818 3.63647 4.47669 3.63647 7.74444C3.63647 11.7998 9.33131 17.7533 9.57377 18.0048C9.80151 18.241 10.1991 18.2406 10.4265 18.0048C10.6689 17.7533 16.3637 11.7998 16.3637 7.74444C16.3637 4.47669 13.509 1.81818 10.0001 1.81818ZM10.0001 10.7261C8.23468 10.7261 6.79844 9.38854 6.79844 7.74444C6.79844 6.10034 8.23472 4.7628 10.0001 4.7628C11.7655 4.7628 13.2017 6.10037 13.2017 7.74447C13.2017 9.38857 11.7655 10.7261 10.0001 10.7261Z" fill="black" fillOpacity="0.35" />
                                                    </svg>
                                                    <span>{address}</span>
                                                </div>
                                                <div className="property-details">
                                                    <div className="detail-row">
                                                        <div className="detail-item">
                                                            <i className="fa-solid fa-ruler-combined"></i>
                                                            <span>{area}</span>
                                                        </div>
                                                        <div className="detail-item">
                                                            <i className="fa-solid fa-bed"></i>
                                                            <span>{bedrooms}</span>
                                                        </div>
                                                        <div className="detail-item">
                                                            <i className="fa-solid fa-bath"></i>
                                                            <span>{bathrooms}</span>
                                                        </div>
                                                        <div className="detail-item">
                                                            <i className="fa-solid fa-car"></i>
                                                            <span>{parking}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="price-type">
                                                    <span className="price">{price !== '—' && price !== '' && !isNaN(Number(price)) ? `€ ${Number(price).toLocaleString()}` : price}</span>
                                                </div>


                                                <div
                                                    className="incoming-member-feed-actions"
                                                    onClick={(e) => e.stopPropagation()}
                                                    onMouseDown={(e) => e.stopPropagation()}
                                                    onTouchStart={(e) => e.stopPropagation()}
                                                >
                                                    <div className="incoming-member-toggle">
                                                        <span>{stringsoflanguages.accepted}</span>
                                                        <label className="imf-switch" onClick={(e) => e.stopPropagation()}>
                                                            <input
                                                                type="checkbox"
                                                                checked={acceptedOverrideByListing?.[toggleKey] ?? isToggleOn(listing.status)}
                                                                onChange={(e) => handleAcceptedToggle(listing, index, e.target.checked)}
                                                                onClick={(e) => e.stopPropagation()}
                                                                onMouseDown={(e) => e.stopPropagation()}
                                                                onTouchStart={(e) => e.stopPropagation()}
                                                            />
                                                            <span className="imf-slider" />
                                                        </label>
                                                    </div>
                                                    {
                                                        listing.business_type == "2" ?
                                                            <div className="incoming-member-b2c-pill" onClick={(e) => e.stopPropagation()}>B to C</div>
                                                            :
                                                            <div className="incoming-member-b2b-pill" onClick={(e) => e.stopPropagation()}>B to B</div>

                                                    }
                                                    <div className="incoming-member-toggle">
                                                        <span>{stringsoflanguages.published}</span>
                                                        <label className="imf-switch" onClick={(e) => e.stopPropagation()}>
                                                            <input
                                                                type="checkbox"
                                                                checked={publishedOverrideByListing?.[toggleKey] ?? isToggleOn(listing.is_published)}
                                                                onChange={(e) => handlePublishedToggle(listing, index, e.target.checked)}
                                                                onClick={(e) => e.stopPropagation()}
                                                                onMouseDown={(e) => e.stopPropagation()}
                                                                onTouchStart={(e) => e.stopPropagation()}
                                                            />
                                                            <span className="imf-slider" />
                                                        </label>
                                                    </div>
                                                </div>

                                            </div>
                                        </div>
                                    </>
                                );
                            })
                        )}
                    </div>
                </div>


            </div>
        </div>
    );
}

